/**
 * Centralized localStorage and sessionStorage keys for the Passport Photo Lab application.
 * All keys are prefixed with "ppl" (passport photo lab) for consistency.
 */

export const SESSION_STORAGE_KEYS = {
  // sessionStorage marker gating the "previous image" offer. Present only within
  // the tab session that stored a previous image, so an auto-update reload (same
  // tab) restores the offer while a fresh tab / new session does not (the actual
  // blob lives in IndexedDB; see previousImageStore.ts).
  PREVIOUS_IMAGE_MARKER: "ppl_previous_image_marker",
};

export const LOCAL_STORAGE_KEYS = {
  // License and activation
  ACTIVATION_KEY: "ppl_activation_key",

  // Stable, opaque per-browser identifier used together with the server-side IP
  // record to detect a single license being used across many machines at once
  // (see src/app/utils/clientId.ts). Written lazily, only once a license key is
  // present — never for demo/never-licensed visitors — and intentionally never
  // removed (it survives license expiry and sign-out).
  CLIENT_ID: "ppl_client_id",

  // Legacy keys (for migration support)
  OLD_ACTIVATION_KEY: "activationKey", // Can be removed in Sept 2026

  // General application settings
  GENERAL_SETTINGS: "ppl_general_settings",
  BG_REMOVAL_DISCLAIMER_SHOWN: "ppl_bg_removal_disclaimer_shown",
  // Breadcrumb set while background removal is running. If it is still present
  // on the next load, the previous attempt crashed the tab (an out-of-memory
  // kill that JavaScript cannot catch, mostly on mobile), so we can warn the
  // user instead of silently resetting. Cleared on success or any caught error.
  BG_REMOVAL_IN_PROGRESS: "ppl_bg_removal_in_progress",

  // Photo standard
  PHOTO_STANDARD: "ppl_photo_standard",

  // Save modal tab selection
  SAVE_TAB: "ppl_save_tab",
  // Set once the user has visited the non-default (Print layout) save tab.
  // Used to stop showing the one-time tab-switch hint.
  SAVE_TAB_OTHER_VISITED: "ppl_save_tab_other_visited",

  // Settings modal: set once the user has visited the non-default (General
  // settings) tab. Used to stop showing the one-time tab-switch hint.
  SETTINGS_TAB_OTHER_VISITED: "ppl_settings_tab_other_visited",

  // Multi-photo save settings
  SAVE_MULTI_LAST_SETTINGS: "ppl_save_multi_last_settings",
  SAVE_MULTI_FAVORITES: "ppl_save_multi_favorites",
  SAVE_MULTI_PRINT_FORMAT: "ppl_save_multi_print_format",
  SAVE_MULTI_DOWNLOAD_FORMAT: "ppl_save_multi_download_format",

  // Alignment system preference (e.g., red lines)
  ALIGNMENT_PREFERENCE: "ppl_alignment_preference",

  // User preferred unit (mm/in)
  USER_PREFERRED_UNIT: "ppl_user_preferred_unit",

  // Audience selection (consumer/pro). Audience selection is stored in GENERAL_SETTINGS.
  // This is a separate key to tell if the audience selection has been explicit.
  AUDIENCE_EXPLICITLY_ASKED: "ppl_audience_explicitly_asked",

  // Marketing and Analytics
  // Marketing consent is asked on first visit if user comes from ad traffic.
  MARKETING_CONSENT: "ppl_marketing_consent",
} as const;

/**
 * Migrates activation key from old localStorage key to new key format.
 * This logic can be removed in September 2026, as by then it shouldn't be
 * possible to have any more valid keys saved with the old localStorage key.
 */
export function migrateActivationKeyIfNeeded(): void {
  const oldKey = localStorage.getItem(LOCAL_STORAGE_KEYS.OLD_ACTIVATION_KEY);
  const currentKey = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVATION_KEY);

  if (oldKey) {
    if (currentKey) {
      // Both keys exist, just delete the old one
      localStorage.removeItem(LOCAL_STORAGE_KEYS.OLD_ACTIVATION_KEY);
    } else {
      // Only old key exists, migrate it to the new key
      localStorage.setItem(LOCAL_STORAGE_KEYS.ACTIVATION_KEY, oldKey);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.OLD_ACTIVATION_KEY);
    }
  }
}
